import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Chip,
  Divider,
  Input,
  LinearProgress,
  Option,
  Select,
  Sheet,
  Skeleton,
  Stack,
  Table,
  Typography,
} from '@mui/joy';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import VolunteerActivismRoundedIcon from '@mui/icons-material/VolunteerActivismRounded';

import MainContent from '../common/MainContent';
import PageHero from '../common/PageHero';
import {
  fetchAdminUser,
  fetchAdminUserDonations,
  type AdminUser,
  type AdminUserDonation,
} from '../../api';

const currencyFormatter = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 0,
});

function getStatusColor(status: string): 'success' | 'danger' | 'warning' | 'neutral' {
  const value = status.toLowerCase();
  if (value.includes('success') || value.includes('paid')) return 'success';
  if (value.includes('fail') || value.includes('error')) return 'danger';
  if (value.includes('pending')) return 'warning';
  return 'neutral';
}

export default function AdminUserDonations() {
  const { id } = useParams();
  const [donations, setDonations] = useState<AdminUserDonation[]>([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<AdminUser | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadDonations = useCallback(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    fetchAdminUserDonations(id)
      .then((data) => setDonations(data ?? []))
      .catch(() => setError('Unable to load offerings for this devotee right now.'))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (!id) return;
    let active = true;
    fetchAdminUser(id)
      .then((data) => {
        if (!active) return;
        setUser(data ?? null);
      })
      .catch(() => {
        if (!active) return;
        setUser(null);
      });
    return () => {
      active = false;
    };
  }, [id]);

  useEffect(() => {
    loadDonations();
  }, [loadDonations]);

  const statuses = useMemo(
    () => Array.from(new Set(donations.map((donation) => donation.status))).filter(Boolean),
    [donations],
  );

  const filteredDonations = useMemo(() => {
    const loweredQuery = search.trim().toLowerCase();
    return donations.filter((donation) => {
      const matchesStatus = !statusFilter || donation.status === statusFilter;
      if (!loweredQuery) return matchesStatus;
      return (
        matchesStatus &&
        [donation.purpose, donation.status, donation.id, donation.amount]
          .map((value) => String(value).toLowerCase())
          .some((value) => value.includes(loweredQuery))
      );
    });
  }, [donations, search, statusFilter]);

  const totalAmount = useMemo(
    () => donations.reduce((sum, donation) => sum + donation.amount, 0),
    [donations],
  );

  const latestDonation = useMemo(() => {
    if (!donations.length) return null;
    return donations
      .slice()
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0];
  }, [donations]);

  if (!id) {
    return (
      <MainContent>
        <PageHero
          eyebrow="Devotee offerings"
          title="Select a devotee to view offerings"
          description="Choose a devotee from the roster so you can lovingly review their history of contributions."
        />
      </MainContent>
    );
  }

  const devoteeName = user?.displayName ?? `Devotee #${id}`;
  const heroDescription = user
    ? `${user.displayName} (${user.email}) has contributed the following offerings. Use the filters to celebrate their seva.`
    : 'Review the offerings recorded for this devotee and gently guide them towards future service opportunities.';

  return (
    <MainContent>
      <PageHero
        eyebrow="Devotee offerings"
        title={`${devoteeName}'s seva ledger`}
        description={heroDescription}
        actions={
          <>
            <Button
              component={RouterLink}
              to={`/users/${id}`}
              variant="outlined"
              color="neutral"
              startDecorator={<ArrowBackRoundedIcon />}
            >
              Back to profile
            </Button>
            <Button
              variant="soft"
              color="neutral"
              startDecorator={<RefreshRoundedIcon />}
              onClick={loadDonations}
              disabled={loading}
            >
              Refresh entries
            </Button>
          </>
        }
      />

      <Stack spacing={3}>
        <Card variant="outlined" sx={{ borderRadius: 'xl', p: { xs: 2.5, md: 3 } }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5} justifyContent="space-between">
            <Stack spacing={0.5}>
              <Typography
                level="title-sm"
                textTransform="uppercase"
                letterSpacing={0.8}
                sx={{ color: 'text.tertiary' }}
              >
                Offerings to date
              </Typography>
              <Typography level="h2" fontWeight={700}>
                {currencyFormatter.format(totalAmount)}
              </Typography>
              <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                Across {donations.length} recorded offerings
              </Typography>
            </Stack>
            <Stack spacing={0.5} minWidth={{ md: 220 }}>
              <Typography
                level="body-xs"
                textTransform="uppercase"
                letterSpacing={0.8}
                sx={{ color: 'text.tertiary' }}
              >
                Latest offering
              </Typography>
              {latestDonation ? (
                <Typography level="title-sm" fontWeight={600}>
                  {currencyFormatter.format(latestDonation.amount)} on{' '}
                  {new Date(latestDonation.date).toLocaleDateString()}
                </Typography>
              ) : (
                <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                  Awaiting their first recorded offering
                </Typography>
              )}
            </Stack>
          </Stack>
          <Divider sx={{ my: 2 }} />
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {statuses.length ? (
              statuses.map((status) => (
                <Chip key={status} size="sm" variant="soft" color={getStatusColor(status)}>
                  {status}
                </Chip>
              ))
            ) : (
              <Chip size="sm" variant="soft" color="neutral">
                No statuses yet
              </Chip>
            )}
          </Stack>
        </Card>

        <Card variant="outlined" sx={{ borderRadius: 'xl', p: { xs: 2.5, md: 3 } }}>
          <Stack spacing={2.5}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
              <Input
                size="md"
                startDecorator={<SearchRoundedIcon />}
                placeholder="Search by purpose, status, or amount"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                sx={{ flexGrow: 1 }}
              />
              <Select
                size="md"
                placeholder="Filter by status"
                value={statusFilter}
                onChange={(_, value) => setStatusFilter(value)}
                sx={{ minWidth: { xs: '100%', md: 220 } }}
              >
                <Option value={null}>All statuses</Option>
                {statuses.map((status) => (
                  <Option key={status} value={status}>
                    {status}
                  </Option>
                ))}
              </Select>
              <Button
                component={RouterLink}
                to="/donate"
                variant="solid"
                color="primary"
                startDecorator={<VolunteerActivismRoundedIcon />}
              >
                Record new offering
              </Button>
            </Stack>

            {error ? (
              <Alert color="danger" variant="soft">
                {error}
              </Alert>
            ) : null}

            <Sheet
              variant="outlined"
              sx={{
                width: '100%',
                borderRadius: 'lg',
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              {loading ? <LinearProgress thickness={3} color="primary" /> : null}
              <Table
                size="md"
                stickyHeader
                borderAxis="bothBetween"
                hoverRow
                sx={{
                  minWidth: 720,
                  '& thead th': { bgcolor: 'background.level1' },
                }}
              >
                <thead>
                  <tr>
                    <th style={{ width: 120 }}>Date</th>
                    <th>ID</th>
                    <th style={{ width: 150 }}>Amount</th>
                    <th>Purpose</th>
                    <th style={{ width: 140 }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {loading
                    ? [...Array(4)].map((_, index) => (
                        <tr key={index}>
                          <td>
                            <Skeleton variant="text" level="body-sm" width="80%" />
                          </td>
                          <td>
                            <Skeleton variant="text" level="body-sm" width="60%" />
                          </td>
                          <td>
                            <Skeleton variant="text" level="body-sm" width="70%" />
                          </td>
                          <td>
                            <Skeleton variant="text" level="body-sm" />
                          </td>
                          <td>
                            <Skeleton variant="rectangular" height={24} sx={{ borderRadius: 'sm' }} />
                          </td>
                        </tr>
                      ))
                    : filteredDonations.length ? (
                        filteredDonations.map((donation) => (
                          <tr key={donation.id}>
                            <td>{new Date(donation.date).toLocaleDateString()}</td>
                            <td>#{donation.id}</td>
                            <td>{currencyFormatter.format(donation.amount)}</td>
                            <td>{donation.purpose}</td>
                            <td>
                              <Chip size="sm" variant="soft" color={getStatusColor(donation.status)}>
                                {donation.status}
                              </Chip>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={5}>
                            <Stack spacing={1} py={3} alignItems="center">
                              <Typography level="title-sm" fontWeight={600}>
                                No offerings match your filters just yet
                              </Typography>
                              <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                                Adjust the search or invite this devotee to make their next heartfelt contribution.
                              </Typography>
                            </Stack>
                          </td>
                        </tr>
                      )}
                </tbody>
              </Table>
            </Sheet>
          </Stack>
        </Card>
      </Stack>
    </MainContent>
  );
}
